/**
 * TriRadarChart - 삼각형 레이더 차트
 * 
 * 기획-리스크-테스트케이스 3가지 축의 충족도를 삼각형으로 시각화
 * MAX 플랜 전용 AI 리포트에 사용
 */

import { useLanguage } from '../i18n';

interface TriRadarChartProps {
  planning: number;     // 기획 점수 (0-100)
  risk: number;         // 리스크 대응 점수 (0-100)
  testCoverage: number; // 테스트 커버리지 점수 (0-100)
  size?: number;        // 차트 크기 (기본값 280)
  animated?: boolean;   // 애니메이션 효과
}

export default function TriRadarChart({ 
  planning, 
  risk, 
  testCoverage, 
  size = 280,
  animated = true 
}: TriRadarChartProps) {
  const { lang } = useLanguage();
  
  const center = size / 2;
  const radius = size * 0.38; // 삼각형 반지름
  
  // 삼각형 꼭짓점 계산 (상단, 우하단, 좌하단)
  // 각도: -90도(상단), 30도(우하단), 150도(좌하단)
  const angles = [-90, 30, 150];
  const points = angles.map(angle => ({
    x: center + radius * Math.cos(angle * Math.PI / 180),
    y: center + radius * Math.sin(angle * Math.PI / 180),
  }));
  
  // 데이터 포인트 계산 (각 축의 비율에 따라)
  const scores = [planning, risk, testCoverage];
  const dataPoints = scores.map((score, i) => {
    const ratio = score / 100;
    const angle = angles[i] * Math.PI / 180;
    return {
      x: center + radius * ratio * Math.cos(angle),
      y: center + radius * ratio * Math.sin(angle),
    };
  });
  
  // 그리드 라인 (25%, 50%, 75%, 100%)
  const gridLevels = [0.25, 0.5, 0.75, 1];
  
  // 삼각형 경로 생성
  const createTrianglePath = (ratio: number) => {
    const pts = angles.map(angle => ({
      x: center + radius * ratio * Math.cos(angle * Math.PI / 180),
      y: center + radius * ratio * Math.sin(angle * Math.PI / 180),
    }));
    return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y} L ${pts[2].x} ${pts[2].y} Z`;
  };
  
  // 데이터 영역 경로
  const dataPath = `M ${dataPoints[0].x} ${dataPoints[0].y} L ${dataPoints[1].x} ${dataPoints[1].y} L ${dataPoints[2].x} ${dataPoints[2].y} Z`;
  
  // 점수 색상 결정
  const getScoreColor = (score: number) => {
    if (score >= 80) return '#10b981'; // 초록
    if (score >= 60) return '#f59e0b'; // 주황
    return '#ef4444'; // 빨강
  };
  
  // 전체 평균 점수
  const avgScore = Math.round((planning + risk + testCoverage) / 3);
  
  // 라벨
  const labels = lang === 'ko' 
    ? ['기획', '리스크 대응', '테스트 커버리지']
    : ['Planning', 'Risk Mgmt', 'Test Coverage'];

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="overflow-visible">
        <defs>
          {/* 그라데이션 */}
          <linearGradient id="dataGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={getScoreColor(avgScore)} stopOpacity="0.6" />
            <stop offset="100%" stopColor={getScoreColor(avgScore)} stopOpacity="0.2" />
          </linearGradient>
          
          {/* 글로우 효과 */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* 배경 그리드 삼각형 */}
        {gridLevels.map((level, i) => (
          <path
            key={i}
            d={createTrianglePath(level)}
            fill="none"
            stroke="currentColor"
            strokeWidth={level === 1 ? 1.5 : 0.5}
            className="text-terminal-border"
            strokeDasharray={level < 1 ? "4 4" : "none"}
          />
        ))}
        
        {/* 축 라인 (중앙에서 꼭짓점으로) */}
        {points.map((point, i) => (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={point.x}
            y2={point.y}
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-terminal-border"
          />
        ))}
        
        {/* 데이터 영역 */}
        <path
          d={dataPath}
          fill="url(#dataGradient)"
          stroke={getScoreColor(avgScore)}
          strokeWidth="2"
          filter="url(#glow)"
          className={animated ? 'animate-in fade-in zoom-in-50 duration-700' : ''}
        />
        
        {/* 데이터 포인트 */}
        {dataPoints.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r="6"
              fill={getScoreColor(scores[i])}
              stroke="white"
              strokeWidth="2"
              className={animated ? 'animate-in fade-in duration-500' : ''}
              style={{ animationDelay: `${i * 150}ms` }}
            />
            {/* 점수 표시 */}
            <text
              x={point.x}
              y={point.y}
              dy="-12"
              textAnchor="middle"
              className="text-xs font-bold fill-current text-terminal-text"
            >
              {scores[i]}
            </text>
          </g>
        ))}
        
        {/* 라벨 */}
        {points.map((_, i) => {
          // 라벨 위치 조정
          const labelOffset = 35;
          const angle = angles[i] * Math.PI / 180;
          const labelX = center + (radius + labelOffset) * Math.cos(angle);
          const labelY = center + (radius + labelOffset) * Math.sin(angle);
          
          return (
            <text
              key={i}
              x={labelX}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs fill-current text-terminal-muted"
            >
              {labels[i]}
            </text>
          );
        })}
        
        {/* 중앙 점수 */}
        <circle
          cx={center}
          cy={center}
          r="24"
          fill="currentColor"
          className="text-terminal-bg"
          stroke={getScoreColor(avgScore)}
          strokeWidth="2"
        />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-lg font-bold fill-current"
          style={{ fill: getScoreColor(avgScore) }}
        >
          {avgScore}
        </text>
      </svg>
      
      {/* 범례 */}
      <div className="flex gap-4 mt-4 text-xs text-terminal-muted">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#10b981]"></span>
          <span>≥80 {lang === 'ko' ? '우수' : 'Good'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#f59e0b]"></span>
          <span>60-79 {lang === 'ko' ? '보통' : 'Fair'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#ef4444]"></span>
          <span>&lt;60 {lang === 'ko' ? '주의' : 'Warn'}</span>
        </div>
      </div>
    </div>
  );
}

